/// <amd-module name="Reporting/BurndownReport/BurndownReport" />
import { Iteration, IterationQuery } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as React from "react";
import { BoardsReport, BoardsReportState } from "Reporting/BoardsCommon/BoardsReport";
import { getSprintsReportUrl } from "Reporting/BoardsCommon/boardsRoutingHelper";
import { LightboxReportGuidedTourUtility } from "Reporting/BoardsCommon/LightboxReportGuidedTourUtility";
import {
    AggregationPicker,
    AggregationSettings,
    ensureAggregationSettings,
    getAggregationOptions,
    WorkItemAggregationOption
} from "Reporting/BoardsCommon/Pickers/AggregationPicker";
import { ensureWorkItemFilter } from "Reporting/BoardsCommon/Pickers/BacklogCategoryPickerHelpers";
import { WorkItemBacklogFilter } from "Reporting/BoardsCommon/Pickers/WorkItemBacklogFilter";
import { getInfoIcon, hasUserSeenBubble, ReportGuidedTourUtility } from "Reporting/BoardsCommon/ReportGuidedTourUtility";
import { resetQueryCache } from "Reporting/BoardsCommon/ReportQueryHelper";
import { publishEmptyReportTelemetry, publishReportTelemetry } from "Reporting/BoardsCommon/ReportTelemetry";
import { ResetElement } from "Reporting/BoardsCommon/ResetElement";
import * as Resources from "Reporting/BurndownReport/Resources";
import { CompactChartErrorWarningComponent } from "Reporting/Visuals/VisualMessages/CompactChartErrorWarningComponent";
import { convertBacklogInfoToFilter, IWorkItemBacklogInfo } from "Reporting/WorkItemsCategoriesPicker/IWorkItemBacklogInfo";
import { AllowedItems, getPickerId, getWorkItemAndCategoriesOptions } from "Reporting/WorkItemsCategoriesPicker/WorkItemsCategoriesHelper";
import { WorkItemsCategoriesPicker } from "Reporting/WorkItemsCategoriesPicker/WorkItemsCategoriesPicker";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { FPSLink, IFPSLinkProps } from "VSS/Features/PlatformUI/FPSLink";
import { NoOpComponent } from "VSS/Platform/Components/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { DatePicker } from "VSSUI/DatePicker";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { daysBetweenDates, parseIsoDateStringInLocalTime, toLocalDateIsoString } from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { AnalyticsSprintBurndownSettings } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettings";
import { assembleBurndownReportSettings } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettingsManager";
import { getLightboxTeachingBubblesData, getReportTeachingBubblesData } from "./BurndownReportTeachingBubbleHelper";
import { BurndownReportUserSettings, BurndownReportUserSettingsData, getBurndownReportSettingsStorageKey } from "./BurndownReportUserSettings";
import { getKnownWarningComponent } from "./NotConfiguredMessages";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { BurndownConstants } from "Widgets/Burndown/BurndownWidgetComponents/BurndownConstants";

export interface BurndownReportProps {
    projectId: string;
    teamId: string;
    teamName: string;
    iterationPath: string;

    /** Only intended for diagnosing AX-synchronization issues where iteration is configured, but not present in AX. */
    iterationStartDate: Date;

    /** Only intended for diagnosing AX-synchronization issues where iteration is configured, but not present in Ax. */
    iterationEndDate: Date;

    /** Requests a refresh of current state. */
    lastRefresh?: Date;

    /** Flag to render minimal header chart*/
    isMinimalChart?: boolean;

    /** Flag to render customized report for lightbox*/
    isLightbox?: boolean;

    /** Signal handler for load completion, used for TTI lifecycle of parent page. */
    onLoadCompleted?: () => void;
}

interface BurndownReportState extends BoardsReportState<AnalyticsSprintBurndownSettings> {
    aggregationOptions: WorkItemAggregationOption[];
    workItemsCategoriesOptions: IListBoxItem<IWorkItemBacklogInfo>[];
    iterationInfo?: Iteration;
    showTeachingBubble?: boolean;
    startDateError: string | null;
    hasCustomUserSettings?: boolean;
    lastRefreshDate?: Date;
}

export class BurndownReport extends BoardsReport<BurndownReportProps, any, BurndownReportState> {
    private reportGuidedTourUtility: ReportGuidedTourUtility;
    private lightboxReportGuidedTourUtility: LightboxReportGuidedTourUtility;

    private taskBacklogCategoryCanceleablePromise: ICancelablePromise<string>;
    private aggregationCanceleablePromise: ICancelablePromise<WorkItemAggregationOption[]>;
    private iterationCanceleablePromise: ICancelablePromise<Iteration>;

    public onChange?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => void;

    constructor(props: BurndownReportProps, context?: IVssComponentContext) {
        super(props, context);
        this.state = this.getDefaultState();
    }

    public getWidgetContributionId(): string {
        return "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.AnalyticsSprintBurndownWidget";
    }

    public getReportTelemetryName(): string {
        return "BurndownReport";
    }

    public getReportTitle(): string {
        return Resources.ReportTitle;
    }

    public isMinimalReport(): boolean | undefined {
        return this.props.isMinimalChart;
    }

    public isLightbox(): boolean | undefined {
        return this.props.isLightbox;
    }

    public initializeOnRender(): void {
        if (this.isLightbox()) {
            this.lightboxReportGuidedTourUtility = new LightboxReportGuidedTourUtility(
                this.context.pageContext,
                getLightboxTeachingBubblesData(),
                this.onBubbleDismiss
            );
        } else {
            this.reportGuidedTourUtility = new ReportGuidedTourUtility(
                this.context.pageContext,
                getReportTeachingBubblesData(),
                this.onBubbleDismiss
            );
        }
    }

    public getTitleBlock(): JSX.Element {
        return <>{super.getTitleBlock(this.renderHeaderChildren())}</>;
    }

    public renderTeachingBubble(): JSX.Element {
        const teachingBubble = this.isLightbox()
            ? this.lightboxReportGuidedTourUtility.render(this.state.showTeachingBubble)
            : this.reportGuidedTourUtility.render(this.state.showTeachingBubble);

        return <>{teachingBubble}</>;
    }

    public decorateWidgetContent(widgetContent: JSX.Element | null): JSX.Element {
        return <div ref={this.reportGuidedTourUtility.chartBubbleCallback}>{widgetContent}</div>;
    }

    public getFilterItems(): JSX.Element {
        if (this.state.widgetSettings === undefined) {
            // Presence of this error is indicative of a basic implementation bug/regression, not a user facing Error to be localized.
            throw new Error("widgetSettings needs to be defined, in order to render Report filter.");
        }
        // Parse ISO date string to local format for user interaction. On picker events we must convert back to AX facing ISO format.
        const startDate = parseIsoDateStringInLocalTime(this.state.widgetSettings.timePeriodConfiguration.startDate);
        const endDate = parseIsoDateStringInLocalTime(this.state.widgetSettings.timePeriodConfiguration.endDate);
        return (
            <>
                <FormItem label={Resources.StartDateLabel}>
                    <DatePicker
                        ariaLabel={Resources.StartDateLabel}
                        value={startDate}
                        onChange={this.onStartDateSelect}
                        hasError={this.state.startDateError !== null}
                        errorMessage={this.state.startDateError || undefined}
                    />
                </FormItem>
                <span className="date-range-pickers-separator">{Resources.To}</span>
                <FormItem label={Resources.EndDateLabel}>
                    <DatePicker ariaLabel={Resources.EndDateLabel} value={endDate} onChange={this.onEndDateSelect} />
                </FormItem>
                <FormItem label={Resources.BacklogsWorkItemsLabel}>
                    <WorkItemsCategoriesPicker
                        items={this.state.workItemsCategoriesOptions}
                        selectedIds={[
                            getPickerId(
                                this.state.widgetSettings.workItemTypeFilter.identifier,
                                this.state.widgetSettings.workItemTypeFilter.settings
                            )
                        ]}
                        onSelect={this.onSelectWorkItemFilter}
                        suppressMultiSelect={true}
                    />
                </FormItem>
                <div ref={this.reportGuidedTourUtility.filterBubbleCallback}>
                    <FormItem label={Resources.AggregateByLabel}>
                        <AggregationPicker
                            value={this.state.widgetSettings.aggregation}
                            items={this.state.aggregationOptions}
                            onSelect={this.onAggregationSelect}
                        />
                    </FormItem>
                </div>
                <FormItem label={Resources.AdvancedLabel}>
                    <Checkbox
                        className="show-non-working-days-checkbox"
                        checked={this.state.widgetSettings.showNonWorkingDays}
                        label={Resources.ShowNonWorkingDaysLabel}
                        onChange={this.onToggleWorkingDays}
                    />
                    <Checkbox
                        checked={this.state.widgetSettings.showRemainingCapacityAtStartOfDay}
                        label={Resources.ShowRemainingCapacityAtStartOfDayLabel}
                        onChange={this.onToggleShowRemainingCapacityAtStartOfDay}
                    />
                    {
                        isFeatureFlagEnabled(this.context.pageContext,BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, false) &&
                        <Checkbox
                            label={this.renderCheckboxLabelWithInfoIcon(Resources.ShowResolvedItemsAsCompletedLabel)}
                            onChange={this.onToggleShowResolvedItemsAsCompleted}
                            checked={this.state.widgetSettings.showResolvedItemsAsCompletedEnabled}
                            tooltipProps={{text: Resources.ShowResolvedItemsAsCompletedLabelToolTipText}}
                        />
                    } 
                </FormItem>
                <ResetElement onClick={this.resetSettings} isEnabled={this.state.hasCustomUserSettings} />
            </>
        );
    }

    /* render ReactNode with labelText and an info icon at end of the text */
    private renderCheckboxLabelWithInfoIcon = (labelText: string) : React.ReactNode => {
        return (
            <div>
                <span>{labelText}</span> &#9432;
            </div>
        )
    }

    public getKnownWarningComponent(): JSX.Element | null {
        if (this.isMinimalReport()) {
            return <CompactChartErrorWarningComponent role="menuitem"/>;
        }

        return getKnownWarningComponent(this.state.knownWarning);
    }

    public getLastRefreshDate(): Date | undefined {
        return this.state.lastRefreshDate;
    }

    public componentDidMount() {
        if (this.isMinimalReport()) {
            // Reset cache when we are preloading as preloading happens post TTI and we should get fresh-est data
            resetQueryCache(this.context.pageContext);
        }
        this.initializeReportState();
    }

    public componentDidUpdate(prevProps: BurndownReportProps) {
        if (JSON.stringify(this.props) !== JSON.stringify(prevProps)) {
            if (this.props.lastRefresh !== prevProps.lastRefresh) {
                resetQueryCache(this.context.pageContext);
            }

            this.setState(this.getDefaultState());
            this.initializeReportState();
        }
    }

    public async initializeReportState() {
        this.cancelDataPromises();

        try {
            const userSettings: BurndownReportUserSettingsData = await BurndownReportUserSettings.load(this.context.pageContext, this.props.teamId);

            const hasUserDismissedBubble = await hasUserSeenBubble(this.context.pageContext, getBurndownReportSettingsStorageKey());

            // Step 1 - Get Team (No-op, Handled by parent)

            // Step 2 - Get WIT Options
            const workItemsCategoriesOptions = await this.trackPromise(
                getWorkItemAndCategoriesOptions(
                    this.context.pageContext,
                    this.props.projectId,
                    this.props.teamId,
                    backlog => {
                        return (
                            backlog.backlogReferenceName === "Microsoft.RequirementCategory" ||
                            backlog.backlogReferenceName === "Microsoft.TaskCategory"
                        );
                    },
                    AllowedItems.BacklogAndWorkItems
                )
            ).promise;

            const workItemTypeFilter: WorkItemBacklogFilter = await this.trackPromise(
                ensureWorkItemFilter(this.context.pageContext, userSettings.workItemTypeFilter as WorkItemBacklogFilter)
            ).promise;

            // Step 3: Get active and available set of aggregation options by workitem in a backlog category or specific work item type
            const aggregationOptions = await this.getAggregationOptions(this.props.projectId, this.props.teamId, workItemTypeFilter);
            const aggregationSettings = ensureAggregationSettings(aggregationOptions, userSettings.aggregation);

            // Step 4 - Get info about active and available Iteration Options (No-op, selection is Handled by parent)
            const path = this.props.iterationPath;
            this.iterationCanceleablePromise = this.trackPromise(
                getIteration(this.context.pageContext, this.props.projectId, path.replace("/", "\\"))
            );

            const iterationInfo = await this.iterationCanceleablePromise.promise;

            const opStoreIterationIsConfigured =
                this.props.iterationPath !== null && this.props.iterationEndDate !== null && this.props.iterationStartDate !== null;

            if (!opStoreIterationIsConfigured) {
                const warning = Resources.WITIterationNotConfiguredWarningDetail;
                this.setState({
                    knownWarning: warning
                });
                publishEmptyReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), warning);
            } else if (iterationInfo == null) {
                const warning = Resources.AxIterationNotFoundWarningDetail;
                this.setState({
                    knownWarning: warning
                });
                publishEmptyReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), warning);
            } else if (iterationInfo.EndDateTimeOffset == null || iterationInfo.StartDateTimeOffset == null) {
                const warning = Resources.AxIterationDatesNotFoundWarningDetail;
                this.setState({
                    knownWarning: warning
                });
                publishEmptyReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), warning);
            } else {
                let startDateString = iterationInfo.StartDateTimeOffset;
                let endDateString = iterationInfo.EndDateTimeOffset;

                // If the user is looking at the last visited iteration, honor custom date selections, otherwise use iteration defaults.
                if (
                    userSettings.iterationId === iterationInfo.IterationSK &&
                    userSettings.startDate !== undefined &&
                    userSettings.endDate !== undefined
                ) {
                    startDateString = userSettings.startDate;
                    endDateString = userSettings.endDate;
                }

                const showNonWorkingDays = userSettings.showNonWorkingDays;
                const showRemainingCapacityAtStartOfDay = userSettings.showRemainingCapacityAtStartOfDay;

                // override value with 'false' if FF is disabled or userSettings.showResolvedItemsAsCompleted is undefined
                const showResolvedItemsAsCompletedUserSettings = isFeatureFlagEnabled(
                    this.context.pageContext,
                    BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, 
                    false) ? userSettings.showResolvedItemsAsCompleted ?? false : false;

                const startDate: Date = parseIsoDateStringInLocalTime(startDateString);
                const endDate: Date = parseIsoDateStringInLocalTime(endDateString);
                const widgetSettings = assembleBurndownReportSettings(
                    this.props.projectId,
                    this.props.teamId,
                    workItemTypeFilter,
                    aggregationSettings,
                    startDate,
                    endDate,
                    iterationInfo,
                    showNonWorkingDays!
                );

                widgetSettings.isMinimalChart = this.props.isMinimalChart!;
                widgetSettings.isLightboxChart = this.props.isLightbox!;
                widgetSettings.showRemainingCapacityAtStartOfDay = showRemainingCapacityAtStartOfDay;
                widgetSettings.showResolvedItemsAsCompletedEnabled = showResolvedItemsAsCompletedUserSettings;

                // TODO: Prioritize saved defaults or URL params driven settings
                const hasCustomUserSettings = JSON.stringify(userSettings) !== "{}";
                this.setState({
                    aggregationOptions: aggregationOptions,
                    workItemsCategoriesOptions: workItemsCategoriesOptions,
                    iterationInfo: iterationInfo,
                    widgetSettings: widgetSettings,
                    showTeachingBubble: !hasUserDismissedBubble,
                    hasCustomUserSettings: hasCustomUserSettings
                });
                if (!this.isMinimalReport()) {
                    // Don't log telemetry for preloading or header chart scenario
                    publishReportTelemetry(this.context.pageContext, this.getReportTelemetryName(), {
                        aggregationMode: widgetSettings.aggregation.identifier, // Aggregation Mode
                        daysInterval: (endDate.valueOf() - startDate.valueOf()) / 86400000, // Dates interval for which data is queried
                        showNonWorkingDays: showNonWorkingDays, // Whether users use non working days displayed on the chart
                        hasCustomUserSettings: hasCustomUserSettings, // Whether users use default settings or customize them
                        workItemBacklogFilterSetting: workItemTypeFilter.identifier, // Whether users filter on backlogs or work item types
                        isLightboxReport: this.isLightbox(), // Helps identify whether lightbox report was loaded or full report
                        showResolvedItemsAsCompleted: widgetSettings.showResolvedItemsAsCompletedEnabled // Whether users use show resolved items as completed feature
                    });
                }
            }
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }
            this.handleCommonInitError(e);
        } finally {
            if (this.props.onLoadCompleted) {
                this.props.onLoadCompleted();
            }
        }
    }

    public async getAggregationOptions(
        projectId: string,
        teamId: string,
        activeWorkItemTypeFilter: WorkItemBacklogFilter
    ): Promise<WorkItemAggregationOption[]> {
        return this.trackPromise(getAggregationOptions(this.context.pageContext, projectId, teamId, activeWorkItemTypeFilter)).promise;
    }

    public getDefaultState(): BurndownReportState {
        return {
            aggregationOptions: [],
            workItemsCategoriesOptions: [],
            iterationInfo: undefined,
            startDateError: null,
            ...super.getDefaultState()
        };
    }

    public isConfigurationValid(): boolean {
        return this.state.startDateError === null;
    }
    public getWidgetName(): string {
        return format(Resources.WidgetNameTemplate, this.props.teamName);
    }

    protected allowPinning(): boolean {
        const allowPinning =
            !this.isMinimalReport() &&
            !this.isLightbox() &&
            isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards-web.sprint-burndown-availability", false);
        return allowPinning;
    }

    protected getWidgetPinningSize() {
        return {
            columnSpan: 4,
            rowSpan: 3
        };
    }

    private renderHeaderChildren(): JSX.Element {
        const showIcon = this.isReady() && !this.isInAbnormalState() && this.isConfigurationValid();
        if (this.isLightbox()) {
            return (
                <>
                    {getInfoIcon(
                        this.lightboxReportGuidedTourUtility.titleBubbleCallback,
                        this.toggleTeachingBubble,
                        showIcon,
                        this.getReportTitle()
                    )}
                    {this.renderRefreshButton()}
                    {<FPSLink {...this.getReportLinkProps()}>{Resources.LightboxReportLinkTitle}</FPSLink>}
                </>
            );
        } else {
            return <>{getInfoIcon(this.reportGuidedTourUtility.titleBubbleCallback, this.toggleTeachingBubble, showIcon, this.getReportTitle())}</>;
        }
    }

    private renderRefreshButton(): JSX.Element {
        return (
            <Button
                className="boards-report-lightbox-refresh-button subtle"
                ariaLabel={Resources.LightboxRefreshButtonLabel}
                iconProps={{ iconName: "Refresh" }}
                onClick={this.refreshData}
            />
        );
    }

    private refreshData = async (): Promise<void> => {
        resetQueryCache(this.context.pageContext);
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Analytics-Reports", this.getReportTelemetryName(), { lighboxRefreshClick: "LightboxRefreshClicked" });
        this.setState({ lastRefreshDate: new Date() });
    };

    private publishViewFullReportClickTelemetry = async (): Promise<void> => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Analytics-Reports", this.getReportTelemetryName(), {
            lightboxViewFullReportClick: "lightboxViewFullReportClick"
        });
    };

    private resetSettings = async (): Promise<void> => {
        // wait to clear out previously saved settings, and re-initialize the report.
        await BurndownReportUserSettings.reset(this.context.pageContext, this.props.teamId);
        this.initializeReportState();
    };

    private onAggregationSelect = (newAggregationField: AggregationSettings): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.aggregation = newAggregationField as ModefulValueSetting<AggregationMode, string>;
        this.setUserSelection(newSettings);
        this.setState({ widgetSettings: newSettings });
    };

    private onToggleWorkingDays = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, showNonWorkingDays: boolean): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.showNonWorkingDays = showNonWorkingDays;
        this.setUserSelection(newSettings);
        this.setState({ widgetSettings: newSettings });
    };

    private onToggleShowRemainingCapacityAtStartOfDay = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, showRemainingCapacityAtStartOfDay: boolean): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.showRemainingCapacityAtStartOfDay = showRemainingCapacityAtStartOfDay;
        this.setUserSelection(newSettings);
        this.setState({ widgetSettings: newSettings });
    };

    private onToggleShowResolvedItemsAsCompleted = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, showResolvedItemsAsCompleted: boolean): void => {
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.showResolvedItemsAsCompletedEnabled = showResolvedItemsAsCompleted;
        this.setUserSelection(newSettings);
        this.setState({ widgetSettings: newSettings });
    };

    private cancelDataPromises(): void {
        if (this.taskBacklogCategoryCanceleablePromise) {
            this.taskBacklogCategoryCanceleablePromise.cancel();
        }
        if (this.aggregationCanceleablePromise) {
            this.aggregationCanceleablePromise.cancel();
        }

        if (this.iterationCanceleablePromise) {
            this.iterationCanceleablePromise.cancel();
        }
    }

    private onStartDateSelect = (newStartDate: Date): void => {
        const endDateString = this.state.widgetSettings!.timePeriodConfiguration.endDate;
        const endDate = parseIsoDateStringInLocalTime(endDateString);

        const newStartDateError = this.getStartDateError(newStartDate, endDate);
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.timePeriodConfiguration.startDate = toLocalDateIsoString(newStartDate);
        if (newStartDateError === null) {
            this.setUserSelection(newSettings);
        }

        this.setState({
            startDateError: newStartDateError,
            widgetSettings: newSettings
        });
    };

    private onEndDateSelect = (newEndDate: Date): void => {
        const startDate = parseIsoDateStringInLocalTime(this.state.widgetSettings!.timePeriodConfiguration.startDate);

        const newStartDateError = this.getStartDateError(startDate, newEndDate);
        const newSettings = { ...this.state.widgetSettings! };
        newSettings.timePeriodConfiguration.endDate = toLocalDateIsoString(newEndDate);
        if (newStartDateError === null) {
            this.setUserSelection(newSettings);
        }

        this.setState({
            startDateError: newStartDateError,
            widgetSettings: newSettings
        });
    };

    private onSelectWorkItemFilter = async (workItemFilter: IListBoxItem<IWorkItemBacklogInfo>[]): Promise<void> => {
        const newSettings = { ...this.state.widgetSettings! };

        // Convert Picker format to simpler settings format.
        const filterSettings: WorkItemBacklogFilter = convertBacklogInfoToFilter(workItemFilter[0].data!);
        newSettings.workItemTypeFilter = filterSettings;
        const teamInfo = this.state.widgetSettings!.team;
        const aggregationOptions = await this.getAggregationOptions(teamInfo.projectId, teamInfo.teamId, filterSettings);
        const aggregationSettings = ensureAggregationSettings(aggregationOptions);
        newSettings.aggregation = aggregationSettings;
        this.setUserSelection(newSettings);
        this.setState({ widgetSettings: newSettings, aggregationOptions: aggregationOptions });
    };

    private toggleTeachingBubble = (): void => {
        this.setState({
            showTeachingBubble: !this.state.showTeachingBubble
        });
    };

    // Need to set state here explicitly to ensure we support teaching bubble load on clicking help icon button
    private onBubbleDismiss = (): void => {
        this.setState({
            showTeachingBubble: false
        });
    };

    private getStartDateError(startDate: Date, endDate: Date): string | null {
        if (startDate.getTime() >= endDate.getTime()) {
            return Resources.StartDateMustBeEarlierThanEndDate;
        }

        if (daysBetweenDates(startDate, endDate) > 180) {
            return Resources.DateRangeExceedsLimit;
        }

        return null;
    }

    private getReportLinkProps(): IFPSLinkProps {
        return {
            className: "burndown-report-lightbox-link body-l",
            href: getSprintsReportUrl(this.context.pageContext),
            onClick: this.publishViewFullReportClickTelemetry
        };
    }

    private setUserSelection(newSettings: AnalyticsSprintBurndownSettings) {
        this.setState({ hasCustomUserSettings: true });
        BurndownReportUserSettings.save(this.context.pageContext, newSettings, this.props.teamId);
    }
}
VssComponent.register("BurndownReport", BurndownReport);
VssComponent.register("BurndownReportPreloader", NoOpComponent);

export async function getIteration(pageContext: IVssPageContext, projectId: string, iterationPath: string): Promise<Iteration> {
    const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
    const items = await queryCache.getCacheableQueryResult(IterationQuery.onIterationPath(projectId, iterationPath));
    return items && items[0];
}
